import { useState } from 'react';
import { MapPin, CheckCircle, XCircle, Truck, Clock, Search, Navigation, User } from 'lucide-react';
import api from '../../../api/axios';
import { mapplsGeocode } from '../../../utils/geocoding';

// Two modes: 'address' (type a text address) | 'gps' (use browser GPS)
export default function DeliveryChecker({ onDeliveryCheck }) {
  const [mode, setMode] = useState('address');
  const [addressInput, setAddressInput] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [error, setError] = useState('');

  const reset = () => {
    setResult(null);
    setResolvedAddress('');
    setError('');
  };

  // mapplsGeocode imported from src/utils/geocoding.js

  const geocodeAndCheck = async () => {
    const trimmed = addressInput.trim();
    if (!trimmed) {
      setError('Please enter an address to check delivery availability.');
      return;
    }

    setGeocoding(true);
    setError('');
    setResult(null);

    try {
      const found = await mapplsGeocode(trimmed);

      if (!found) {
        setError('Location not found. Try using the GPS button, or enter a more specific address.');
        return;
      }

      setResolvedAddress(found.display_name);
      // Pass the user-typed address so parent can auto-fill its address field
      await checkDelivery(found.lat, found.lng, found.display_name, trimmed);
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Could not look up the address. Please check your connection and try again.');
    } finally {
      setGeocoding(false);
    }
  };

  // --- GPS: get browser coordinates ---
  const getGpsLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }
    setChecking(true);
    setError('');
    setResult(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        checkDelivery(position.coords.latitude, position.coords.longitude, 'Your current location');
      },
      () => {
        setChecking(false);
        setError('Unable to get your location. Please enable location services or enter an address instead.');
      }
    );
  };

  // --- Call delivery API ---
  const checkDelivery = async (latitude, longitude, label = '', typedAddress = '') => {
    setChecking(true);
    setError('');

    try {
      const response = await api.post('/medical-tests/check-delivery/', { latitude, longitude });

      if (response.data.success) {
        const deliveryData = {
          ...response.data,
          coordinates: { latitude, longitude },
          addressLabel: label,
          // Original text typed by user — parent form can use to auto-fill its address field
          typedAddress,
        };
        setResult(deliveryData);
        if (onDeliveryCheck) onDeliveryCheck(deliveryData);
      } else {
        setError('Unable to verify delivery for this location.');
      }
    } catch (err) {
      console.error('Delivery check error:', err);
      setError('Failed to check delivery availability. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const busy = geocoding || checking;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        Check Home Collection Availability
      </h3>
      <p className="text-sm text-gray-500 mb-5">
        Enter the collection address — even if you're booking for someone else.
      </p>

      {/* Mode tabs */}
      {!result && (
        <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-5">
          <button
            onClick={() => { setMode('address'); setError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
              mode === 'address'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <User className="w-4 h-4" />
            Enter Address
          </button>
          <button
            onClick={() => { setMode('gps'); setError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors border-l border-gray-200 ${
              mode === 'gps'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Navigation className="w-4 h-4" />
            Use My Location
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* ---- Address input mode ---- */}
      {!result && mode === 'address' && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={addressInput}
                onChange={(e) => setAddressInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !busy && geocodeAndCheck()}
                placeholder="e.g. 12 MG Road, Bengaluru, Karnataka"
                disabled={busy}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>
            <button
              onClick={geocodeAndCheck}
              disabled={busy}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                busy
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow'
              }`}
            >
              {busy ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              {geocoding ? 'Looking up…' : checking ? 'Checking…' : 'Check'}
            </button>
          </div>
          <p className="text-xs text-gray-400">
            Include street, city, state, or pincode for the best results.
          </p>
        </div>
      )}

      {/* ---- GPS mode ---- */}
      {!result && mode === 'gps' && (
        <div className="text-center py-4">
          <Navigation className="w-12 h-12 text-blue-500 mx-auto mb-3" />
          <p className="text-gray-600 text-sm mb-5">
            We'll use your device's GPS to check availability at your current location.
          </p>
          <button
            onClick={getGpsLocation}
            disabled={busy}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              busy
                ? 'bg-gray-300 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
            }`}
          >
            {checking ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Navigation className="w-5 h-5" />
            )}
            {checking ? 'Checking…' : 'Detect My Location'}
          </button>
        </div>
      )}

      {/* ---- Result ---- */}
      {result && (
        <div className="space-y-4">
          {/* Resolved address label */}
          {result.addressLabel && (
            <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
              <MapPin className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span className="leading-snug">{result.addressLabel}</span>
            </div>
          )}

          {/* Availability status */}
          <div className={`flex items-center gap-3 p-4 rounded-lg ${
            result.available
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            {result.available ? (
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
            ) : (
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
            )}
            <div>
              <h4 className={`font-medium ${result.available ? 'text-green-900' : 'text-red-900'}`}>
                {result.available ? 'Home Collection Available!' : 'Home Collection Not Available'}
              </h4>
              <p className={`text-sm ${result.available ? 'text-green-700' : 'text-red-700'}`}>
                {result.available
                  ? 'We can collect samples from this address.'
                  : 'Please visit our nearest center for sample collection.'}
              </p>
            </div>
          </div>

          {/* Stats */}
          {result.available && (
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Truck className="w-6 h-6 text-blue-500 mx-auto mb-1.5" />
                <p className="text-xs font-medium text-gray-500 mb-0.5">Collection Charge</p>
                <p className="text-base font-semibold text-blue-600">
                  ₹{(parseFloat(result.delivery_charge) || 0).toFixed(2)}
                </p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-6 h-6 text-green-500 mx-auto mb-1.5" />
                <p className="text-xs font-medium text-gray-500 mb-0.5">Distance</p>
                <p className="text-base font-semibold text-green-600">
                  {(parseFloat(result.distance_km) || 0).toFixed(1)} km
                </p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Clock className="w-6 h-6 text-orange-500 mx-auto mb-1.5" />
                <p className="text-xs font-medium text-gray-500 mb-0.5">Est. Time</p>
                <p className="text-base font-semibold text-orange-600">
                  {result.estimated_time || '—'}
                </p>
              </div>
            </div>
          )}

          {/* Re-check */}
          <div className="text-center pt-2">
            <button
              onClick={reset}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium underline underline-offset-2"
            >
              Check a Different Address
            </button>
          </div>
        </div>
      )}
    </div>
  );
}